'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Copy, CheckCheck, MailCheck, X } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

function traduzirErro(message: string | undefined): string {
  if (!message) return '';
  const m = message.toLowerCase();
  if (m.includes('invalid login') || m.includes('invalid credentials') || m.includes('wrong password')) return 'E-mail ou senha incorretos. Verifique suas credenciais.';
  if (m.includes('email not confirmed') || m.includes('email_not_confirmed')) return 'E-mail não confirmado. Verifique sua caixa de entrada.';
  if (m.includes('user not found') || m.includes('no user found')) return 'Usuário não encontrado.';
  if (m.includes('too many requests') || m.includes('rate limit')) return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
  if (m.includes('network') || m.includes('fetch')) return 'Erro de conexão. Verifique sua internet e tente novamente.';
  if (m.includes('user already registered') || m.includes('email already')) return 'Este e-mail já está cadastrado.';
  if (m.includes('password should be at least')) return 'A senha deve ter pelo menos 6 caracteres.';
  if (m.includes('weak password')) return 'Senha muito fraca. Use letras maiúsculas, minúsculas, números e símbolos.';
  if (m.includes('invalid email')) return 'E-mail inválido.';
  if (m.includes('signup disabled')) return 'Cadastro temporariamente desabilitado.';
  if (m.includes('email link is invalid or has expired') || m.includes('token has expired') || m.includes('invalid token')) return 'Link inválido ou expirado. Solicite um novo.';
  if (m.includes('new password should be different')) return 'A nova senha deve ser diferente da senha atual.';
  if (m.includes('auth session missing') || m.includes('session_not_found')) return 'Sessão expirada. Faça login novamente.';
  return message;
}

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const DEMO_CREDENTIALS = {
  email: 'ana.vieira@rd2sign.com.br',
  password: 'RD2Sign@2026',
};

interface Props {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [emailNotConfirmed, setEmailNotConfirmed] = useState<string | null>(null);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const router = useRouter();
  const { signIn, resetPassword } = useAuth();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const emailValue = watch('email');

  const handleCopy = async (field: 'email' | 'password') => {
    const val = DEMO_CREDENTIALS[field];
    await navigator.clipboard.writeText(val);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleFillDemo = () => {
    setValue('email', DEMO_CREDENTIALS.email);
    setValue('password', DEMO_CREDENTIALS.password);
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setEmailNotConfirmed(null);
    try {
      await signIn(data.email, data.password);
      toast.success('Login realizado com sucesso!');
      router.push('/solicitante-dashboard');
    } catch (err: any) {
      const message = err?.message || '';
      if (
        message.toLowerCase().includes('email not confirmed') ||
        message.toLowerCase().includes('email_not_confirmed')
      ) {
        // User registered but hasn't confirmed email yet
        setEmailNotConfirmed(data.email);
      } else if (
        message.toLowerCase().includes('invalid login') ||
        message.toLowerCase().includes('invalid credentials') ||
        message.toLowerCase().includes('wrong password')
      ) {
        setError('email', {
          message: 'E-mail ou senha incorretos. Verifique suas credenciais.',
        });
      } else {
        toast.error(traduzirErro(message) || 'Erro ao fazer login. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      await resetPassword(forgotEmail);
      setForgotSent(true);
    } catch (err: any) {
      toast.error(traduzirErro(err?.message) || 'Erro ao enviar e-mail de recuperação.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleCloseForgotModal = () => {
    setShowForgotModal(false);
    setForgotEmail('');
    setForgotSent(false);
    setForgotLoading(false);
  };

  // Show email not confirmed banner
  if (emailNotConfirmed) {
    return (
      <div className="animate-slide-up">
        <div className="bg-amber-900/30 border border-amber-500/40 rounded-2xl p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
            <MailCheck size={28} className="text-amber-400" />
          </div>
          <h3 className="text-[16px] font-bold text-white mb-2">
            E-mail não confirmado
          </h3>
          <p className="text-[13px] text-white/60 mb-4 leading-relaxed">
            Você precisa confirmar seu e-mail antes de acessar o portal. Verifique sua caixa de entrada.
          </p>
          <button
            onClick={() => router.push(`/email-confirmation?email=${encodeURIComponent(emailNotConfirmed)}`)}
            className="w-full py-2.5 bg-amber-500 text-white rounded-lg font-600 text-[14px] hover:bg-amber-600 transition-colors mb-3"
          >
            Ver instruções de confirmação
          </button>
          <button
            onClick={() => setEmailNotConfirmed(null)}
            className="text-[13px] text-white/50 hover:text-white transition-colors"
          >
            Voltar ao login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-slide-up">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Bem-vindo de volta</h2>
        <p className="text-white/50 text-[14px] mt-1">
          Entre na sua conta para gerenciar seus documentos
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-[13px] font-600 text-white/80 mb-1.5">
            E-mail
          </label>
          <input
            type="email"
            autoComplete="email"
            placeholder="seu@email.com.br"
            {...register('email', {
              required: 'E-mail é obrigatório',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'E-mail inválido' },
            })}
            className={`w-full px-3.5 py-2.5 rounded-lg border text-[14px] bg-white/5 text-white placeholder-white/30 transition-colors outline-none
              focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db]
              ${errors.email ? 'border-red-500/60 bg-red-900/10' : 'border-white/10'}`}
          />
          {errors.email && (
            <p className="text-red-400 text-[12px] mt-1">{errors.email.message}</p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-[13px] font-600 text-white/80">Senha</label>
            <a
              href="#"
              onClick={e => { e.preventDefault(); setShowForgotModal(true); }}
              className="text-[12px] text-[#60a5fa] hover:underline font-500"
            >
              Esqueceu a senha?
            </a>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              {...register('password', {
                required: 'Senha é obrigatória',
                minLength: { value: 8, message: 'Mínimo 8 caracteres' },
              })}
              className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border text-[14px] bg-white/5 text-white placeholder-white/30 transition-colors outline-none
                focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db]
                ${errors.password ? 'border-red-500/60 bg-red-900/10' : 'border-white/10'}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(s => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-400 text-[12px] mt-1">{errors.password.message}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="rememberMe"
            {...register('rememberMe')}
            className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#1a56db] cursor-pointer"
          />
          <label htmlFor="rememberMe" className="text-[13px] text-white/50 cursor-pointer">
            Manter conectado por 30 dias
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 bg-[#1a56db] hover:bg-[#1e40af] text-white rounded-lg font-600 text-[14px]
            shadow-lg shadow-[#1a56db]/30 hover:shadow-[#1a56db]/50
            active:scale-[0.98] transition-all duration-150
            disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Entrando...
            </>
          ) : 'Entrar na conta'}
        </button>
      </form>

      <p className="text-center text-[13px] text-white/50 mt-5">
        Não tem uma conta?{' '}
        <button onClick={onSwitchToSignup} className="text-[#60a5fa] font-600 hover:underline">
          Assinar Plano
        </button>
      </p>

      {/* Demo credentials */}
      <div className="hidden mt-6 rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-700 uppercase tracking-widest text-white/40">
            Conta de demonstração
          </p>
          <button
            onClick={handleFillDemo}
            className="text-[11px] font-600 text-[#60a5fa] hover:underline"
          >
            Preencher automaticamente
          </button>
        </div>
        <div className="space-y-2">
          {(['email', 'password'] as const).map(field => (
            <div key={`cred-${field}`} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2 border border-white/10">
              <span className="text-[11px] text-white/40 w-12 shrink-0 font-500">
                {field === 'email' ? 'E-mail' : 'Senha'}
              </span>
              <span className="flex-1 font-mono text-[12px] text-white/70 truncate">
                {DEMO_CREDENTIALS[field]}
              </span>
              <button
                onClick={() => handleCopy(field)}
                className="text-white/40 hover:text-[#60a5fa] transition-colors"
                aria-label={`Copiar ${field}`}
              >
                {copiedField === field ? <CheckCheck size={13} className="text-[#34d399]" /> : <Copy size={13} />}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-[#1a1f2e] border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-600 text-[16px]">Recuperar senha</h3>
              <button
                type="button"
                onClick={handleCloseForgotModal}
                className="text-white/40 hover:text-white transition-colors"
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
            </div>

            {forgotSent ? (
              <div className="text-center py-4">
                <MailCheck size={40} className="text-[#60a5fa] mx-auto mb-3" />
                <p className="text-white font-600 text-[15px] mb-1">E-mail enviado!</p>
                <p className="text-white/60 text-[13px]">
                  Verifique sua caixa de entrada em <span className="text-white/80 font-500">{forgotEmail}</span> e siga as instruções para redefinir sua senha.
                </p>
                <button
                  type="button"
                  onClick={handleCloseForgotModal}
                  className="mt-5 w-full py-2.5 rounded-lg bg-[#1a56db] hover:bg-[#1e40af] text-white text-[14px] font-600 transition-colors"
                >
                  Fechar
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-white/60 text-[13px]">
                  Informe seu e-mail cadastrado e enviaremos um link para redefinir sua senha.
                </p>
                <div>
                  <label className="block text-[13px] font-600 text-white/80 mb-1.5">E-mail</label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    placeholder="seu@email.com"
                    required
                    className="w-full px-3.5 py-2.5 rounded-lg border border-white/10 text-[14px] bg-white/5 text-white placeholder-white/30 outline-none focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db] transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={forgotLoading || !forgotEmail}
                  className="w-full py-2.5 rounded-lg bg-[#1a56db] hover:bg-[#1e40af] disabled:opacity-50 disabled:cursor-not-allowed text-white text-[14px] font-600 transition-colors flex items-center justify-center gap-2"
                >
                  {forgotLoading ? (
                    <><Loader2 size={16} className="animate-spin" /> Enviando...</>
                  ) : (
                    'Enviar link de recuperação'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}